/**
 * SafetySection Component
 * Safety observations and checklists for daily logs
 * BuilderBel Construction Project Management System
 */

import {
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Plus,
  Shield,
  X,
} from 'lucide-react';
import React from 'react';
import { Control, Controller, FieldErrors, useWatch } from 'react-hook-form';
import type { SafetyEntry } from '../../../types/dailyLog';
import { Input, NumberInput, TextArea } from '../../forms';

// Common toolbox talk topics
const TOOLBOX_TOPICS = [
  'Fall Protection',
  'Electrical Safety',
  'Ladder Safety',
  'Scaffolding Safety',
  'Personal Protective Equipment',
  'Fire Prevention',
  'Housekeeping',
  'Tool Safety',
  'Confined Space',
  'Excavation Safety',
  'Crane Safety',
  'Forklift Safety',
  'Heat Illness Prevention',
  'Cold Weather Safety',
  'Hazard Communication',
  'Emergency Procedures',
  'First Aid',
  'Silica Dust Exposure',
  'Respiratory Protection',
  'Noise Protection',
];

// Common hazards
const COMMON_HAZARDS = [
  'Trip hazard',
  'Slip hazard',
  'Fall hazard',
  'Electrical hazard',
  'Fire hazard',
  'Chemical exposure',
  'Dust exposure',
  'Noise exposure',
  'Overhead work',
  'Moving equipment',
  'Confined space',
  'Excavation',
  'Sharp edges',
  'Hot surfaces',
  'Weather conditions',
];

interface SafetySectionProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: FieldErrors<any>;
  isExpanded: boolean;
  onToggle: () => void;
  disabled?: boolean;
}

// Tag input component for arrays
const TagInput: React.FC<{
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  datalist?: string[];
  listId?: string;
}> = ({ value = [], onChange, disabled, placeholder, datalist, listId }) => {
  const [inputValue, setInputValue] = React.useState('');

  const handleAdd = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
      setInputValue('');
    }
  };

  const handleRemove = (index: number) => {
    const newValue = [...value];
    newValue.splice(index, 1);
    onChange(newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div>
      {/* Tags */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {value.map((item, index) => (
            <span
              key={index}
              className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded"
            >
              {item}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemove(index)}
                  className="ml-1 text-gray-400 hover:text-red-500"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Input */}
      {!disabled && (
        <div className="flex gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            list={listId}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm"
          />
          {datalist && listId && (
            <datalist id={listId}>
              {datalist
                .filter(item => !value.includes(item))
                .map(item => (
                  <option key={item} value={item} />
                ))}
            </datalist>
          )}
          <button
            type="button"
            onClick={handleAdd}
            className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export const SafetySection: React.FC<SafetySectionProps> = ({
  control,
  errors,
  isExpanded,
  onToggle,
  disabled = false,
}) => {
  const safetyErrors = errors.safety as FieldErrors<SafetyEntry> | undefined;

  // Watch safety values for summary
  const safety = useWatch({ control, name: 'safety' }) as Partial<SafetyEntry> | undefined;

  const hazardCount = safety?.hazardsIdentified?.length || 0;
  const nearMissCount = safety?.nearMisses?.length || 0;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Section Header */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between text-left hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <Shield className="h-5 w-5 text-green-600" />
          <span className="font-medium text-gray-900">Safety</span>
        </div>
        <div className="flex items-center space-x-4">
          {/* Safety Summary */}
          <div className="flex items-center space-x-3">
            {safety?.toolboxTalkConducted && (
              <span className="flex items-center text-xs text-green-600">
                <CheckCircle className="h-4 w-4 mr-1" />
                Toolbox Talk
              </span>
            )}
            {hazardCount > 0 && (
              <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded">
                {hazardCount} hazards
              </span>
            )}
            {nearMissCount > 0 && (
              <span className="px-2 py-0.5 bg-red-100 text-red-800 text-xs rounded">
                {nearMissCount} near misses
              </span>
            )}
          </div>
          {isExpanded ? (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronRight className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </button>

      {/* Section Content */}
      {isExpanded && (
        <div className="p-4 space-y-6 bg-white">
          {/* Toolbox Talk */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-gray-900 border-b pb-2">
              Toolbox Talk / Safety Meeting
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Conducted Checkbox */}
              <Controller
                name="safety.toolboxTalkConducted"
                control={control}
                render={({ field }) => (
                  <label className="flex items-center space-x-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={field.value || false}
                      onChange={field.onChange}
                      disabled={disabled}
                      className="h-5 w-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <span className="text-sm text-gray-700">Toolbox Talk Conducted</span>
                  </label>
                )}
              />

              {/* Topic */}
              <Controller
                name="safety.toolboxTalkTopic"
                control={control}
                render={({ field }) => (
                  <div>
                    <Input
                      label="Topic"
                      value={field.value || ''}
                      onChange={field.onChange}
                      error={safetyErrors?.toolboxTalkTopic?.message}
                      disabled={disabled}
                      placeholder="Select or enter topic"
                      list="toolbox-topic-options"
                    />
                    <datalist id="toolbox-topic-options">
                      {TOOLBOX_TOPICS.map(topic => (
                        <option key={topic} value={topic} />
                      ))}
                    </datalist>
                  </div>
                )}
              />

              {/* Attendees */}
              <Controller
                name="safety.toolboxTalkAttendees"
                control={control}
                render={({ field }) => (
                  <NumberInput
                    label="Attendees"
                    value={field.value}
                    onChange={field.onChange}
                    error={safetyErrors?.toolboxTalkAttendees?.message}
                    disabled={disabled}
                    min={0}
                    placeholder="Number of attendees"
                  />
                )}
              />
            </div>
          </div>

          {/* Compliance Checks */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-gray-900 border-b pb-2">
              Compliance & Inspections
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* PPE Compliance */}
              <Controller
                name="safety.ppeCompliance"
                control={control}
                render={({ field }) => (
                  <label className="flex items-center space-x-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={field.value || false}
                      onChange={field.onChange}
                      disabled={disabled}
                      className="h-5 w-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <div>
                      <span className="text-sm text-gray-700 font-medium">PPE Compliance</span>
                      <p className="text-xs text-gray-500">All workers wearing required PPE</p>
                    </div>
                  </label>
                )}
              />

              {/* Site Inspection */}
              <Controller
                name="safety.siteInspectionCompleted"
                control={control}
                render={({ field }) => (
                  <label className="flex items-center space-x-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={field.value || false}
                      onChange={field.onChange}
                      disabled={disabled}
                      className="h-5 w-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <div>
                      <span className="text-sm text-gray-700 font-medium">
                        Site Inspection Completed
                      </span>
                      <p className="text-xs text-gray-500">Daily safety walkthrough performed</p>
                    </div>
                  </label>
                )}
              />
            </div>
          </div>

          {/* Observations & Hazards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Safety Observations */}
            <div className="space-y-2">
              <label className="block text-xs font-medium text-gray-500">Safety Observations</label>
              <Controller
                name="safety.observations"
                control={control}
                render={({ field }) => (
                  <TagInput
                    value={field.value || []}
                    onChange={field.onChange}
                    disabled={disabled}
                    placeholder="Add observation..."
                  />
                )}
              />
            </div>

            {/* Hazards Identified */}
            <div className="space-y-2">
              <label className="flex items-center text-xs font-medium text-gray-500">
                <AlertTriangle className="h-3 w-3 mr-1 text-yellow-500" />
                Hazards Identified
              </label>
              <Controller
                name="safety.hazardsIdentified"
                control={control}
                render={({ field }) => (
                  <TagInput
                    value={field.value || []}
                    onChange={field.onChange}
                    disabled={disabled}
                    placeholder="Add hazard..."
                    datalist={COMMON_HAZARDS}
                    listId="hazard-options"
                  />
                )}
              />
            </div>

            {/* Corrective Actions */}
            <div className="space-y-2">
              <label className="flex items-center text-xs font-medium text-gray-500">
                <CheckCircle className="h-3 w-3 mr-1 text-green-500" />
                Corrective Actions Taken
              </label>
              <Controller
                name="safety.correctiveActions"
                control={control}
                render={({ field }) => (
                  <TagInput
                    value={field.value || []}
                    onChange={field.onChange}
                    disabled={disabled}
                    placeholder="Add corrective action..."
                  />
                )}
              />
            </div>

            {/* Near Misses */}
            <div className="space-y-2">
              <label className="flex items-center text-xs font-medium text-gray-500">
                <AlertTriangle className="h-3 w-3 mr-1 text-red-500" />
                Near Misses
              </label>
              <Controller
                name="safety.nearMisses"
                control={control}
                render={({ field }) => (
                  <TagInput
                    value={field.value || []}
                    onChange={field.onChange}
                    disabled={disabled}
                    placeholder="Add near miss..."
                  />
                )}
              />
            </div>
          </div>

          {/* Notes */}
          <Controller
            name="safety.notes"
            control={control}
            render={({ field }) => (
              <TextArea
                label="Safety Notes"
                value={field.value || ''}
                onChange={field.onChange}
                error={safetyErrors?.notes?.message}
                disabled={disabled}
                rows={3}
                placeholder="Additional safety observations, concerns, or recommendations..."
              />
            )}
          />
        </div>
      )}
    </div>
  );
};

export default SafetySection;
